import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './App.css';

import logoImage from './zdjecia/Inne/logo.png';

import { CATALOG, findProductByKey } from './catalog';
import ProductSection from './ProductSection';
import ProductDetail from './ProductDetail';
import ApiarySection from './ApiarySection';
import Navbar from './Navbar';

const GalleryView = React.lazy(() => import('./GalleryView'));

// Filmy w sekcji hero (honey2 jest wyróżniony i pokazywany też na mobile).
const HERO_VIDEOS = [
  { id: 'honey1', src: '/videos/honey1.mp4', index: 0, featured: false },
  { id: 'honey2', src: '/videos/honey2.mp4', index: 1, featured: true },
  { id: 'honey3', src: '/videos/honey3.mp4', index: 2, featured: false }
];

const PRELOADER_DURATION_MS = 3000;
const LIST_TITLE = 'Przytulna Pasieka — naturalne miody';

// Odczytuje widok detali bezpośrednio z adresu URL (#produkt/nektarowe-3),
// dzięki czemu linki do produktu są bezpośrednie i działają po odświeżeniu.
const readDetailFromHash = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const hash = window.location.hash || '';
  const match = /^#produkt\/(.+)$/.exec(hash);
  return match ? findProductByKey(match[1]) : null;
};

// Widok galerii „Z życia pasieki": hash #galeria renderuje osobny ekran
// (ten sam wzorzec co detal produktu), żeby zdjęcia i filmy nie obciążały startu.
const readGalleryFromHash = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return (window.location.hash || '') === '#galeria';
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [detail, setDetail] = useState(readDetailFromHash);
  const [isGallery, setIsGallery] = useState(readGalleryFromHash);
  // true, gdy honey2 (wyróżnione wideo hero) wczytał już pierwszą klatkę —
  // do tego czasu pokazujemy komunikat „Wczytuje się film…".
  const [heroVideoReady, setHeroVideoReady] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 768px)').matches
  ));

  const revealRefs = useRef([]);
  const observerRef = useRef(null);
  const videoRefs = useRef([]);
  const videoRefCallbacks = useRef({});
  const wasInSubpage = useRef(false);
  // Pozycja strony głównej zapamiętana przy wejściu w podwidok (+ sekcja
  // katalogu produktu jako awaryjny cel powrotu). null = jesteśmy na głównej.
  const mainViewPos = useRef(null);
  // Zamiar kliknięcia „#sklep": 'section' (navbar, hero-btn, scroll-indicator)
  // albo 'return' (.detail-back-btn). Te linki mają identyczny href, a znaczą
  // co innego – pierwsze prowadzą na początek sekcji, drugi wraca tam, gdzie
  // użytkownik ostatnio przewijał. Domyślnie 'return', bo nawigacja bez
  // kliknięcia (Wstecz/Dalej) to ten sam gest co „Powrót".
  const shopIntent = useRef('return');

  // Stabilna referencja -> React nie odpina i nie przypina wideo przy każdym renderze.
  const getVideoRef = (index) => {
    if (!videoRefCallbacks.current[index]) {
      videoRefCallbacks.current[index] = (el) => {
        videoRefs.current[index] = el;
      };
    }
    return videoRefCallbacks.current[index];
  };

  // Zbiera elementy do animacji „reveal”. Sprząta odpięte elementy
  // (sekcje są odmontowywane przy wejściu w widok detali) i od razu
  // obserwuje nowo dodane, żeby animacja zadziałała także po powrocie.
  const addToRefs = useCallback((el) => {
    revealRefs.current = revealRefs.current.filter((node) => {
      if (node.isConnected) return true;
      if (observerRef.current) observerRef.current.unobserve(node);
      return false;
    });

    if (!el) return;
    if (!revealRefs.current.includes(el)) revealRefs.current.push(el);

    if (observerRef.current) {
      observerRef.current.observe(el);
    } else if (typeof IntersectionObserver !== 'function') {
      el.classList.add('visible');
    }
  }, []);

  const pageReady = !isLoading;
  const heroVideos = isMobile
    ? [{ ...HERO_VIDEOS[1], preload: 'metadata' }]
    : HERO_VIDEOS.map((video) => ({ ...video, preload: 'auto' }));

  // Ekran ładowania: zawsze chowany po stałym czasie.
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, PRELOADER_DURATION_MS);

    return () => clearTimeout(timer);
  }, []);

  // Animacje pojawiania się sekcji przy przewijaniu.
  useEffect(() => {
    const targets = revealRefs.current;

    // Bez IntersectionObserver treść nie mogłaby się pojawić – pokazujemy ją od razu.
    if (typeof IntersectionObserver !== 'function') {
      targets.forEach((el) => el.classList.add('visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );

    observerRef.current = observer;
    targets.forEach((el) => observer.observe(el));

    return () => {
      observerRef.current = null;
      observer.disconnect();
    };
  }, []);

  // Nawigacja: hash steruje widokiem. Popstate obsługuje Wstecz/Prawo
  // przeglądarki po nawigacji przez history.pushState (hashchange wtedy nie
  // wystrzeli), np. powrót „do sklepu" z detalu.
  useEffect(() => {
    const handleHistoryChange = () => {
      const nextDetail = readDetailFromHash();
      const nextGallery = readGalleryFromHash();

      // Pozycję trzeba zapamiętać ZANIM React zamieni stronę główną na
      // podwidok: po renderze dokument się skraca, a przeglądarka przycina
      // scrollY do nowego, mniejszego zakresu. Odczyt tu (hashchange/popstate)
      // łapie jeszcze prawdziwą pozycję w pełnym dokumencie strony głównej.
      if ((nextDetail || nextGallery) && mainViewPos.current === null) {
        mainViewPos.current = {
          y: window.scrollY,
          sectionId: nextDetail ? nextDetail.section.id : 'pasieka'
        };
      }

      setDetail(nextDetail);
      setIsGallery(nextGallery);
    };

    window.addEventListener('hashchange', handleHistoryChange);
    window.addEventListener('popstate', handleHistoryChange);
    return () => {
      window.removeEventListener('hashchange', handleHistoryChange);
      window.removeEventListener('popstate', handleHistoryChange);
    };
  }, []);

  // Przechwycenie kotwic „#sklep" tylko wtedy, gdy wracamy z podwidoku
  // (mamy zapisaną pozycję). Blokujemy natywny skok przeglądarki do fragmentu,
  // bo startuje on po naszym płynnym przywracaniu pozycji i nadpisuje je –
  // lądujemy wtedy na początku sekcji zamiast w miejscu, w którym użytkownik
  // przerwał scrollowanie. Scroll wykonuje efekt nawigacji poniżej.
  useEffect(() => {
    const handleShopClick = (event) => {
      if (mainViewPos.current === null) {
        return;
      }
      const element = event.target instanceof Element ? event.target : null;
      const anchor = element ? element.closest('a[href="#sklep"]') : null;
      if (!anchor) {
        return;
      }
      event.preventDefault();
      shopIntent.current = anchor.classList.contains('detail-back-btn')
        ? 'return'
        : 'section';
      window.history.pushState(null, '', '#sklep');
      setDetail(null);
      setIsGallery(false);
    };

    document.addEventListener('click', handleShopClick);
    return () => document.removeEventListener('click', handleShopClick);
  }, []);

  // Tytuł zakładki odzwierciedla aktualny widok (dobrze dla SEO i historii).
  useEffect(() => {
    if (detail) {
      document.title = `${detail.product.name} — ${LIST_TITLE}`;
    } else if (isGallery) {
      document.title = `Z życia pasieki — ${LIST_TITLE}`;
    } else {
      document.title = LIST_TITLE;
    }
  }, [detail, isGallery]);

  // Po wejściu w detail lub galerię przechodzimy na górę; po powrocie
  // decyduje shopIntent – link do sekcji wchodzi na jej początek, a
  // „Powrót" przywraca zapamiętowaną pozycję strony głównej.
  // Pozycja jest zapisywana w handleHistoryChange PRZED zamianą widoku
  // (po renderze dokument już się skraca i scrollY jest przycięty do
  // mniejszego zakresu) – tutaj tylko uzupełniamy sekcję dla wejścia
  // z linku/odświeżenia, bo wtedy pozycji jeszcze nie ma.
  // Layout, nie effect: przy powrocie z podwidoku strona główna montuje się
  // z scrollY = 0, czyli na hero. Przewijamy w fazie layoutu, żeby hero nie
  // zdążyło się pomalować – inaczej pierwsza klatka pokazuje jego dół.
  useLayoutEffect(() => {
    if (detail || isGallery) {
      if (mainViewPos.current === null) {
        mainViewPos.current = {
          y: 0,
          sectionId: detail ? detail.section.id : 'pasieka'
        };
      }
      wasInSubpage.current = true;
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    if (wasInSubpage.current) {
      wasInSubpage.current = false;
      const anchor = window.location.hash.slice(1);
      const target = anchor ? document.getElementById(anchor) : null;
      const { y, sectionId } = mainViewPos.current || { y: 0, sectionId: null };
      mainViewPos.current = null;

      const intent = shopIntent.current;
      shopIntent.current = 'return';

      const scrollToTarget = (el) => {
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      };

      // Link do sekcji (navbar, hero-btn, scroll-indicator): zawsze początek
      // sekcji – nigdy zapamiętany piksel, bo dla użytkownika to wybór
      // sekcji, a nie powrót do miejsca, z którego odszedł do podwidoku.
      // Przeskok, nie smooth: animowane przewijanie pokazałoby całe hero,
      // a sekcje i tak dociągają się animacją reveal po IntersectionObserver.
      if (intent === 'section') {
        scrollToTarget(target || document.getElementById('sklep'));
        return;
      }

      // „Powrót": dokładne miejsce przerwania przewijania, o ile
      // wciąż mieści się w dokumencie – strona główna montuje się od nowa
      // i jej wysokość może się zmienić, więc offset przycinamy do maksimum.
      if (y > 0) {
        const maxY = document.documentElement.scrollHeight - window.innerHeight;
        if (y <= maxY) {
          window.scrollTo({ top: y, behavior: 'auto' });
          return;
        }
      }

      // Offset nieosiągalny (wejście z adresu/odświeżenie albo skrócona
      // strona) – wchodzimy na sekcję katalogu, z której pochodził produkt.
      scrollToTarget((sectionId ? document.getElementById(sectionId) : null) || target);
    }
  }, [detail, isGallery]);

  // Po powrocie na stronę główną wideo hero montuje się na nowo i znów
  // potrzebuje chwili na wczytanie pierwszej klatki — odtwarzamy komunikat
  // „Wczytuje się film…" (zdarzenie loadeddata ukryje go ponownie).
  useEffect(() => {
    if (detail || isGallery) {
      return;
    }
    setHeroVideoReady(false);
  }, [detail, isGallery]);

  // Automatyczny start filmów w hero po zakończeniu ładowania.
  useEffect(() => {
    if (!pageReady) {
      return undefined;
    }

    videoRefs.current.forEach((video) => {
      if (video && typeof video.play === 'function') {
        video.play().catch((error) => {
          console.warn('Hero video could not start automatically.', error);
        });
      }
    });

    return undefined;
  }, [pageReady, isMobile]);

  // Nasłuch zmian szerokości ekranu (mobile/desktop).
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mobileQuery = window.matchMedia('(max-width: 768px)');
    const handleViewportChange = (event) => setIsMobile(event.matches);

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleViewportChange);
    }

    return () => {
      if (typeof mobileQuery.removeEventListener === 'function') {
        mobileQuery.removeEventListener('change', handleViewportChange);
      }
    };
  }, []);

  return (
    <div className="app-container">
      <div className="page-ambient" aria-hidden="true" />

      <Navbar view={isGallery ? 'galeria' : detail ? 'produkt' : 'strona'} />

      <div className={`bee-preloader ${pageReady ? 'fade-out' : ''}`}>
        <div className="preloader-content">
          <div className="bee-animation-wrapper">
            {/* Logo.png (czarna pszczoła) przekolorowane na złoto marki filtrem
                CSS — patrz .preloader-logo; animacja ruchu jest na wrapperze,
                żeby klatki beeFlight (drop-shadow) nie nadpisywały koloru. */}
            <img src={logoImage} className="preloader-logo" alt="" />
          </div>
          <p className="preloader-title">PRZYTULNA PASIEKA</p>
        </div>
      </div>

      {detail ? (
        <main className="app-main">
          <ProductDetail product={detail.product} />
        </main>
      ) : isGallery ? (
        <main className="app-main">
          <React.Suspense fallback={<p className="gallery-loading">Ładowanie galerii…</p>}>
            <GalleryView />
          </React.Suspense>
        </main>
      ) : (
        <main className="app-main">
          <section className="hero" id="strona">
            <div className="hero-overlay"></div>

            {/* Komunikat „Wczytuje się film…" – dopóki honey2 nie wczyta
                pierwszej klatki, wideo renderuje się przezroczysto i napis
                jest widoczny; znika z chwilą zdarzenia loadeddata. Ustawiony
                pod tytułem, nad wskaźnikiem scrolla – nie nachodzi na treść. */}
            {!heroVideoReady && (
              <div className="hero-video-loading" aria-hidden="true">
                <p className="hero-video-loading-text">Wczytuje się film…</p>
              </div>
            )}

            <div className={`hero-videos ${pageReady ? 'is-ready' : ''}`}>
              {heroVideos.map(({ id, src, index, featured, preload }) => (
                <div
                  key={`${isMobile ? 'mobile' : 'desktop'}-${id}`}
                  className={featured ? 'video-column featured-video' : 'video-column'}
                >
                  <video
                    ref={getVideoRef(index)}
                    autoPlay={pageReady}
                    muted
                    loop
                    playsInline
                    preload={preload}
                    onLoadedData={featured ? () => setHeroVideoReady(true) : undefined}
                  >
                    <source src={src} type="video/mp4" />
                  </video>
                </div>
              ))}
            </div>

            <div className={`hero-content ${pageReady ? 'is-ready' : ''}`}>
              <span className="hero-subtitle">NATURALNE MIODY Z POLSKIEJ PASIEKI</span>
              <h1 className="hero-title">PRZYTULNA PASIEKA</h1>
              <div className="hero-divider"></div>
              <a href="#sklep" className="hero-btn">Poznaj naszą ofertę</a>
            </div>

            <a href="#sklep" className="scroll-indicator" aria-label="Odkryj stronę">
              <span className="scroll-text">Odkryj więcej</span>
              <div className="mouse-icon">
                <div className="wheel"></div>
              </div>
              <svg className="chevron-down" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </a>
          </section>

          <section className="products-section" id="sklep">
            <h2 className="visually-hidden">Sklep</h2>
            {CATALOG.map((section) => (
              <ProductSection key={section.id} section={section} addToRefs={addToRefs} />
            ))}
          </section>

          <ApiarySection addToRefs={addToRefs} />

          <section className="contact-section" id="kontakt">
            <div className="contact-header reveal" ref={addToRefs}>
              <h2>Bądźmy w kontakcie</h2>
              <div className="premium-line-divider"></div>
              <p className="contact-intro">Napisz do nas lub zadzwoń – z radością odpowiemy na każde pytanie.</p>
            </div>

            <div className="contact-grid reveal" ref={addToRefs}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- tymczasowy, pusty href */}
              <a href="" target="_blank" rel="noreferrer" className="new-contact-tile card-delay-1">
                <div className="tile-icon-box">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a.598.598 0 01-.655-.57c-.045-.643.086-1.28.379-1.844A8.251 8.251 0 013 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                  </svg>
                </div>
                <h3>Messenger</h3>
                <p>Odpowiadamy na wiadomości na bieżąco. Napisz do nas na Facebooku!</p>
                <span className="tile-action-arrow">Wyślij wiadomość &rarr;</span>
              </a>

              <a href="tel:+48 123 456 678" className="new-contact-tile card-delay-2">
                <div className="tile-icon-box">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.387a12.035 12.035 0 01-7.108-7.108c-.155-.44.01-1.29.387-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                </div>
                <h3>Zadzwoń do nas</h3>
                <p>Porozmawiaj bezpośrednio. Chętnie doradzimy i przyjmiemy zamówienie.</p>
                <span className="tile-action-arrow">+48 123 456 678</span>
              </a>

              <a href="mailto:@gmail.com" className="new-contact-tile card-delay-3">
                <div className="tile-icon-box">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0l-7.5-4.615a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                </div>
                <h3>Napisz maila</h3>
                <p>Wyślij wiadomość o dowolnej porze. Przygotujemy ofertę dedykowaną.</p>
                <span className="tile-action-arrow">@gmail.com</span>
              </a>
            </div>
          </section>
        </main>
      )}

      <footer className="footer-bottom">
        <nav className="footer-social-links" aria-label="Znajdź nas w internecie">
          <a href="/" target="_blank" rel="noreferrer">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="1" className="social-icon-fill" />
            </svg>
            Instagram
          </a>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- tymczasowy, pusty href */}
          <a href="" target="_blank" rel="noreferrer">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14.8 4.2h2.1V1.1c-.36-.05-1.6-.16-3.05-.16-3.02 0-5.1 1.84-5.1 5.22v2.91H5.4v3.47h3.35v10.2h4.1v-10.2h3.2l.51-3.47h-3.71V6.54c0-1 .28-1.68 1.95-1.68z" />
            </svg>
            Facebook
          </a>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- tymczasowy, pusty href */}
          <a href="" target="_blank" rel="noreferrer">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1116 0z" />
              <circle cx="12" cy="10" r="2.5" />
            </svg>
            Google Maps
          </a>
        </nav>
        <p>&copy; 2026 Przytulna Pasieka. Wszystkie prawa zastrzeżone.</p>
      </footer>
    </div>
  );
}

export default App;
